/**
 * Avatars and presence (docs/design/visual-overhaul.md §7.8, C18, T17, R4):
 * the generated colour pairs, initials (one letter, larger, for Arabic
 * script), whole-pixel presence dots per size, and the speaking ring. The
 * app wraps Avatar with its image-URL config (apps/client/src/ui/kit.tsx).
 */
import { memo, type CSSProperties } from 'react';

/**
 * The pairs of 58ceeb8 (owner, 2026-10-29: the vivid gradients are the look),
 * drawn top-left to bottom-right; the initials carry a soft shadow so they
 * read on the light ends (avatar.css).
 */
export const PALETTE: ReadonlyArray<readonly [string, string]> = [
  ['#8b5cf6', '#22d3ee'],
  ['#f97316', '#e11d48'],
  ['#10b981', '#0891b2'],
  ['#6366f1', '#a855f7'],
  ['#ec4899', '#8b5cf6'],
  ['#0ea5e9', '#2563eb'],
  ['#f59e0b', '#ea580c'],
  ['#14b8a6', '#16a34a'],
  ['#e11d48', '#9f1239'],
  ['#64748b', '#334155'],
  ['#a855f7', '#db2777'],
  ['#06b6d4', '#3b82f6'],
  ['#65a30d', '#0f766e'],
  ['#f43f5e', '#f59e0b'],
  ['#7c3aed', '#1d4ed8'],
  ['#0e7490', '#155e75'],
];

/** A stable palette index for an ID (FNV-1a). */
export function paletteIndex(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) % PALETTE.length;
}

/** --a1/--a2 for an avatar, a voice tile or a profile banner. */
export function gradStyle(id: string): CSSProperties {
  const p = PALETTE[paletteIndex(id)] ?? PALETTE[0]!;
  return { '--a1': p[0], '--a2': p[1] } as CSSProperties;
}

const ARABIC = /\p{Script=Arabic}/u;

/** Initials: two Latin letters, or one letter for Arabic script (T17). */
export function initials(name: string): string {
  const s = String(name || '?').trim();
  if (!s) return '?';
  if (ARABIC.test(s[0] ?? '')) return s[0] ?? '?';
  const words = s
    .replace(/[._-]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  const first = words[0] ?? s;
  let r = first[0] ?? '?';
  const second = words[1];
  if (second && /[A-Z]/.test(second[0] ?? '')) r += second[0];
  else {
    const m = first.slice(1).match(/[A-Z]/);
    if (m) r += m[0];
  }
  return r.toUpperCase();
}

/** "arab" when the initials are Arabic script (avatar.css sizes them up), else undefined. */
export function initialsScript(text: string): 'arab' | undefined {
  return ARABIC.test(text) ? 'arab' : undefined;
}

/** The presence dot's diameter: a whole, even number of px per avatar size (§2.2 (6)). */
export function presenceDot(size: number): number {
  if (size <= 24) return 8;
  if (size <= 32) return 10;
  if (size <= 48) return 12;
  if (size <= 64) return 16;
  if (size <= 80) return 20;
  return 24;
}

export type PresenceKind = 'online' | 'idle' | 'dnd' | 'offline';

export function Presence({
  status,
  solo,
}: {
  status: PresenceKind | 'invisible' | 'streaming';
  solo?: boolean;
}) {
  return (
    <i
      className={'nx-pres' + (solo ? ' solo' : '')}
      data-p={status === 'invisible' ? 'offline' : status}
    />
  );
}

export interface AvatarProps {
  /** Seeds the colour pair. */
  id: string;
  name: string;
  size: number;
  /** An absolute image URL, or nothing for the initials. */
  src?: string | null;
  presence?: PresenceKind | 'invisible' | 'streaming' | null;
  speaking?: boolean;
  cls?: string;
  label?: string;
}

/** The style an avatar of `size` needs: its size, dot and colour pair. */
export function avatarStyle(id: string, size: number): CSSProperties {
  return {
    '--s': `${size}px`,
    '--pd': `${presenceDot(size)}px`,
    ...gradStyle(id),
  } as CSSProperties;
}

export const Avatar = memo(function Avatar({
  id,
  name,
  size,
  src,
  presence,
  speaking,
  cls,
  label,
}: AvatarProps) {
  const c =
    'nx-av' +
    (presence ? ' has-p' : '') +
    (speaking ? ' is-speaking' : '') +
    (cls ? ' ' + cls : '');
  const text = src ? '' : initials(name);
  return (
    <span
      className={c}
      style={avatarStyle(id, size)}
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
    >
      <span className="nx-av-in" data-script={src ? undefined : initialsScript(text)}>
        {src ? <img src={src} alt="" width={size} height={size} className="nx-av-img" /> : text}
      </span>
      {presence ? <Presence status={presence} /> : null}
    </span>
  );
});
